/**
 * Ações da IA do atendimento numa conversa (`social_ai_actions`) e as decisões
 * da equipe sobre elas pela Edge `ia-acoes` (aprovar / recusar / desfazer /
 * aprovar as pendentes). Fase 2 do atendente — doc
 * docs/funcionalidades/atendente-ia-whatsapp.md.
 *
 * Escrita em `social_ai_actions` é só do servidor (RLS: staff só lê); por isso
 * toda decisão passa pela Edge, que confere o staff do clube da linha.
 */
import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { qk } from '@pps/query-keys';
import { cleanPayload, extractEdgeError } from '@pps/supabase/payload';
import { supabase } from '@/integrations/supabase/client';
import i18n from '@/i18n';
import { PRAZO_PARA_LIBERAR_MS, STATUS_EM_CURSO, textoDoMotivo, type AcaoDaIa, type StatusDaAcaoDaIa } from '@/lib/acoes-da-ia';

const COLUNAS = 'id, ferramenta, status, entrada, resultado, erro, created_at, executada_em, em_curso_desde';
/** Executadas/falhas desta janela aparecem como cartão no chat; as mais velhas só na ficha. */
const JANELA_RECENTE_MS = 12 * 60 * 60 * 1000;
const LIMITE = 30;

/** `liberar` = linha parada em executando/desfazendo há mais de 5 min → falhou (interrompida). */
export type OperacaoNaAcao = 'aprovar' | 'recusar' | 'desfazer' | 'liberar';

interface Desfecho {
  ok: boolean;
  acao_id: string;
  status?: StatusDaAcaoDaIa;
  motivo?: string;
}

function comoObjeto(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {};
}

/**
 * Lê as ações da conversa. `marca` muda quando chega novidade na conversa
 * (sugestão nova, mensagem nova — `social_conversations` tem realtime no inbox):
 * aí a lista é relida. Fora isso, relê a cada 30 s como a lista de conversas.
 */
export function useAcoesDaIa(conversationId: string | null, marca?: string) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: qk.whatsapp.acoesDaIa(conversationId),
    enabled: !!conversationId,
    refetchInterval: 30000,
    queryFn: async (): Promise<AcaoDaIa[]> => {
      const { data, error } = await supabase
        .from('social_ai_actions')
        .select(COLUNAS)
        .eq('conversation_id', conversationId!)
        .order('created_at', { ascending: false })
        .limit(LIMITE);
      if (error) throw error;
      const agora = Date.now();
      const desde = agora - JANELA_RECENTE_MS;
      return (data ?? []).map((l) => ({
        id: l.id,
        ferramenta: l.ferramenta,
        status: l.status as StatusDaAcaoDaIa,
        entrada: comoObjeto(l.entrada),
        resultado: comoObjeto(l.resultado),
        erro: l.erro,
        created_at: l.created_at,
        executada_em: l.executada_em,
        recente: new Date(l.created_at).getTime() >= desde,
        parada:
          STATUS_EM_CURSO.has(l.status as StatusDaAcaoDaIa) &&
          agora - new Date(l.em_curso_desde ?? l.created_at).getTime() >= PRAZO_PARA_LIBERAR_MS,
      }));
    },
  });

  // Novidade na conversa → relê. A 1ª marca é a da montagem (a query já está
  // buscando): só as mudanças seguintes invalidam.
  const marcaAnterior = useRef(marca);
  useEffect(() => {
    if (marcaAnterior.current === marca) return;
    marcaAnterior.current = marca;
    if (conversationId) qc.invalidateQueries({ queryKey: qk.whatsapp.acoesDaIa(conversationId) });
  }, [conversationId, marca, qc]);

  return query;
}

/** Corpo JSON do erro da Edge (FunctionsHttpError.context), sem consumir o original. */
async function corpoDoErro(err: unknown): Promise<Record<string, unknown> | null> {
  const ctx = (err as { context?: Response })?.context;
  if (!ctx || typeof ctx.clone !== 'function') return null;
  try {
    return comoObjeto(await ctx.clone().json());
  } catch {
    return null;
  }
}

/**
 * Chama a `ia-acoes`. Falha vira `Error` com o texto do motivo já traduzido —
 * é o que o toast de erro do useDomainMutation mostra na descrição.
 */
export async function chamarIaAcoes(
  corpo: { acao_id: string; operacao: OperacaoNaAcao } | { conversation_id: string; operacao: 'aprovar_pendentes' },
): Promise<Desfecho[]> {
  const { data, error } = await supabase.functions.invoke('ia-acoes', { body: cleanPayload(corpo) });
  if (error) {
    const corpoErro = await corpoDoErro(error);
    const codigo = await extractEdgeError(corpoErro ?? error);
    if (codigo === 'conflict') {
      const falhas = comoObjeto(corpoErro?.details).falhas;
      const motivo = Array.isArray(falhas) ? comoObjeto(falhas[0]).motivo : null;
      throw new Error(textoDoMotivo(typeof motivo === 'string' ? motivo : null, (k, o) => i18n.t(k, o)));
    }
    if (codigo === 'forbidden') throw new Error(i18n.t('common.forbidden'));
    if (codigo === 'rate_limited') throw new Error(i18n.t('common.rateLimited'));
    throw new Error(i18n.t('whatsapp.iaAcoes.motivos.erro_interno'));
  }
  const desfechos = comoObjeto(data).desfechos;
  return Array.isArray(desfechos) ? (desfechos as Desfecho[]) : [];
}

/** Aprovar / recusar / desfazer UMA ação (cartão no chat ou na ficha). */
export function useDecidirAcaoDaIa() {
  const qc = useQueryClient();
  return useDomainMutation({
    mutationFn: async ({ acaoId, operacao }: { acaoId: string; operacao: OperacaoNaAcao }) => {
      await chamarIaAcoes({ acao_id: acaoId, operacao });
    },
    invalidates: qk.whatsapp.acoesDaIaAll,
    // Sem toast de sucesso: o cartão muda de estado na hora (relido).
    toast: { error: i18n.t('whatsapp.iaAcoes.erroNaDecisao') },
    tags: { feature: 'crm', action: 'decidir_acao_da_ia' },
    // A falha também muda a linha (falhou / continua executada): relê.
    onError: () => {
      qc.invalidateQueries({ queryKey: qk.whatsapp.acoesDaIaAll });
    },
  });
}
